import {useEffect, useState} from "react";
import axios from "axios";
import {BrowserRouter, Routes, Route} from "react-router-dom";
import {motion, AnimatePresence} from "framer-motion";
import {
    ArrowUpRight, ArrowRight, BarChart3, Bot, Boxes, Check, ChevronDown,
    CircleDollarSign, Fingerprint, Gem, GitBranch, LockKeyhole, Network,
    Radar, ScrollText, ShieldCheck, Sparkles, WalletCards, Waves, Zap,
    Sun, Moon,
} from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imageUrls = {
    hero: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&w=1800&q=85",
    analytics: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/09298f342186d63fd4a97fefe3cf24ac156c8219b4ab572f6b2e60c69bb8c77f.jpeg",
    yield: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/c400998b57e77edaf38ef29b33940ac8dc0c2f5bd6bb0b382df4a8713b1e0bdc.jpeg",
    vault: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/14a63ccd86982317a5725421c4c874c64e2fc40dc61cb89fb5279d85b61e266a.jpeg",
    terminal: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/18db753155bc76a48e945a48b8bd27ee923fc5f6dda53e56c6c0c101b66c0778.jpeg",
    passkey: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/c0312d8052ca739ec0a93013315340ffc86a2a23a0e9d4aa51e00f1a3d6536fe.jpeg",
    chain: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/988f2b60f9043d311f4b997c0167522df3fe43012675dc42c59a66168326edc6.jpeg",
    ledger: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/5371f4a7ce8d5dd6fac3fc6339a440239bea28aaae95ed769a814f7f92f7938d.jpeg",
    compound: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/915571b928a2b8b9b4ca5149378eade7b9820a6329349a18389c1cf08207ec83.jpeg",
    ai: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/4ab603d6c490098aa5f9c658a50759267b1ad17014784c097b413290cee6f998.jpeg",
    growth: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/0597f1c7765c80aaa150b1e20ce6cb2962cc1be642561fdd6e2c8d9843666815.jpeg",
    citadel: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/4efd173ba3e75692bba6f9191b525631091aaa09ea8ad6892cc8df22fc7ec0e4.jpeg",
    military: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/e63274a1bd2d2e8f6d8f77d79a15e856359c68bc44110644d9ac95bbc51c3929.jpeg",
    compliance: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/caba2fe2a016ee1a362f6f85d23fc71b4037323e1241b10cda6dffd1756a34fb.jpeg",
    inheritance: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/8c97895e593f5f8f43fd824b57633a3a0adef64f9fb148218f82c97f5016233b.jpeg",
    zkp: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/88c14a311b2b8dc21b11ca0eebb0cc7e13a6028ece9ac138cfda08ac96c73d2c.jpeg",
    routing: "https://static.prod-images.emergentagent.com/jobs/19a1a85d-876a-449d-ab87-670c386e3c09/images/7713359f2651ae46efc2c250a5273acc8a7b53b6e1231db337ff607d606bb1e0.jpeg",
};

const featureImages = {
    "YIELD ENGINE": imageUrls.yield,
    "SOVEREIGN VAULT": imageUrls.vault,
    "EXECUTION TERMINAL": imageUrls.terminal,
    "PASSKEY SECURITY": imageUrls.passkey,
    "MULTI-CHAIN NETWORK": imageUrls.chain,
    "DIGITAL LEDGER": imageUrls.ledger,
    "Foreclosure Engine": imageUrls.vault,
    "Liquidity Market": imageUrls.ledger,
    "Treasury Routing": imageUrls.chain,
    "Military Defense": imageUrls.passkey,
    Automation: imageUrls.compound,
    "Neural Shield": imageUrls.ai,
    Gamification: imageUrls.growth,
    "Multi-Chain": imageUrls.chain,
    "Global Feed": imageUrls.analytics,
    "AI Companion": imageUrls.ai,
    "Proof Engine": imageUrls.ledger,
    Analytics: imageUrls.analytics
};

const modules = [
    ["Foreclosure Engine", "Long-Term Investment Foreclosure", "The Problem We Solved:", "Traditional platforms trap your capital for months with severe penalties if you need emergency cash. Tradverse introduces an automated smart-contract foreclosure engine, converting active long-term deposits into digital yield certificates so you can unlock instant liquidity without breaking your tenor commitments.", ["Multi-Tenor Fixed Vaults (90 - 365 Days)", "Zero Tenor Break Penalties"], LockKeyhole],
    ["Liquidity Market", "P2P Yield Certificate Market", "Absolute Financial Freedom:", "Never stay stranded during financial crunches. List your active long-term vault deposit NFTs on our internal P2P escrow marketplace to instantly retrieve an 85% cash liquidity payout while buyers take over your ongoing yield stream.", ["Automated Escrow Atomic Swaps", "Immediate Cash Access"], ScrollText],
    ["Treasury Routing", "Advanced Dual-Wallet Separate Routing", "Zero Cross-Contamination:", "Ordinary exchanges mix short-term trading liquidity with long-term capital reserves, creating systemic risks. Tradverse pioneers complete treasury separation, ensuring high-frequency daily payouts and long-term funds clear through isolated gateways.", ["Daily Trading Payout Channel", "Tiered & Long-Term Secure Gateway"], GitBranch],
    ["Military Defense", "FIDO2 Hardware Biometric Passkeys", "Zero Password Theft:", "We completely eradicate vulnerable email passwords and SMS 2FA. Tradverse pioneers device-native fingerprint and FaceID enclaves, ensuring nobody else on earth can authorize transactions from your account.", ["Zero Phishing Vulnerability", "Google Authenticator 2FA Integration"], Fingerprint],
    ["Automation", "Auto-Compound Sniper Bot", "Set-and-Forget Compounding:", "Manual compounding introduces human delay and missed yield windows. Our autonomous micro-second sniper bot constantly monitors accrued vault profits and instantly loops them back into active streams.", ["Micro-Second Sweeps", "Exponential Asset Scaling"], Bot],
    ["Neural Shield", "AI Risk & Yield Rebalancing Matrix", "Proactive Defense:", "Powered by ANPR v4 neural architecture, our system constantly scans global liquidity corridors, tuning yield multipliers upward during bull runs and triggering Capital Shield stablecoin buffers during dips.", ["Proactive Volatility Parsing", "Dynamic Yield Optimization"], Radar],
    ["Gamification", "Affiliate Partner & Network Tracker", "Career Progression in Web3:", "Automatically evaluates user accumulation based on stored portal database metrics (Direct A referrals and indirect Team B + C referrals) to calculate and upgrade account tier levels seamlessly.", [], BarChart3],
    ["Multi-Chain", "Institutional Multi-Chain Wallet & P/L", "Unified Control:", "A unified treasury dashboard supporting TRON (TRC-20), Binance Smart Chain (BEP-20), and Ethereum (ERC-20) networks with individual net profit/loss tracking calculated precisely from join dates.", ["Multi-Chain Gateway", "Detailed P/L Statements"], Network],
    ["Global Feed", "100+ Crypto Marquee Ticker Floor", "Global Scope:", "An infinite scrolling global liquidity floor tracking over 100+ digital assets simultaneously with live green/red price action indicators and automated WebSocket synchronization.", ["Continuous Asset Scrolling", "Institutional Breadth"], Waves],
    ["AI Companion", "AI Anime Trading Companion (\"Kira\")", "Immersive UX:", "A gamified AI persona widget (\"Kira\") built right into the user dashboard, delivering live sentiment updates, bullish momentum alerts, and whale tracking telemetry in an engaging format.", ["Live Sentiment Dialogues", "Whale Tracking Telemetry"], Sparkles],
    ["Proof Engine", "Verified Arbitrage & Fund Ledger", "Complete Proof:", "A real-time transaction ledger cross-checking multi-crypto micro-allocations with live Binance historical profit validation, ensuring complete transparency of every trade executed by Tradverse nodes.", ["API Profit Cross-Checking", "Infinite Scroll Telemetry"], ShieldCheck],
    ["Analytics", "Live Market Watch & Spline Feeds", "Institutional Visibility:", "Real-time Binance WebSocket streaming ticker feeds, interactive historical kline data, and dynamic timeframe filters providing institutional-grade market visibility.", ["Sub-Second Ticker Streaming", "Interactive Spline Trends"], BarChart3],
];

const faqs = [
    ["How do the 8-Hour Core Pooling cycles operate?", "Our institutional block pooling executes across rolling 8-hour windows, ensuring precise market entries, compound execution, and total capital security with structured level progressions."],
    ["What makes Tradverse completely free of counterparty risk?", "Tradverse implements strict treasury separation, FIDO2 hardware biometric passkeys, and zero-knowledge cryptographic verification so your funds remain exclusively under your control."],
    ["How does the Dead-Man's Switch nominee protocol function?", "It is an autonomous zero-trust protocol. In the event of prolonged inactivity, your pre-registered nominee can securely claim your digital wealth using encrypted passkeys without login credentials."],
    ["Are the market rates and crypto ticker feeds real-time?", "Yes, our marquee ticker floor operates on a 100% live real-time Binance WebSocket and REST API pipeline, tracking over 20+ core digital assets continuously."],
];

function Reveal({children, className = ""}) {
    return <motion.div className={className} initial={{opacity: 0, y: 24}} whileInView={{opacity: 1, y: 0}}
                       viewport={{once: true, margin: "-80px"}} transition={{duration: .7}}>{children}</motion.div>;
}

function Visual({type = "chart"}) {
    const images = {chart: imageUrls.yield, vault: imageUrls.vault, terminal: imageUrls.terminal};
    return <div className={`live-visual visual-${type}`} aria-hidden="true"><img
        src={images[type] || imageUrls.analytics} alt=""/>
        <div className="visual-scan"/>
        <div className="visual-badge"><span className="live-dot"/> LIVE SYSTEM / BUILDING</div>
    </div>;
}

function FeatureArt({src, label}) {
    return <div className="feature-art" aria-hidden="true"><img src={src} alt=""/>
        <div className="art-scan"/>
        <div className="art-caption"><span className="live-dot"/> {label} / LIVE BUILD</div>
    </div>;
}

const buildStages = [
    ["LAYING THE FOUNDATION", "Sovereign treasury bedrock secured", 16],
    ["FORGING SOVEREIGN VAULTS", "Tier-1 isolated escrow rising", 41],
    ["DEPLOYING NEURAL SHIELD", "ANPR v4 defense matrix online", 67],
    ["SEALING THE CITADEL GATES", "FIDO2 passkey enclaves fused", 89],
    ["GENESIS READY", "Awaiting private access launch", 100],
];

const LAUNCH_TS = new Date("2026-11-01T00:00:00Z").getTime();
const pad = (n) => String(n).padStart(2, "0");

function useCountdown() {
    const [left, setLeft] = useState(() => Math.max(0, LAUNCH_TS - Date.now()));
    useEffect(() => {
        const id = setInterval(() => setLeft(Math.max(0, LAUNCH_TS - Date.now())), 1000);
        return () => clearInterval(id);
    }, []);
    return {
        d: Math.floor(left / 86400000),
        h: Math.floor(left / 3600000) % 24,
        m: Math.floor(left / 60000) % 60,
        s: Math.floor(left / 1000) % 60
    };
}

function CitadelBuild() {
    const [step, setStep] = useState(0);
    const {d, h, m, s} = useCountdown();
    useEffect(() => {
        const id = setInterval(() => setStep((v) => (v + 1) % buildStages.length), 2600);
        return () => clearInterval(id);
    }, []);
    const [label, sub, pct] = buildStages[step];
    return (
        <div className="command-card citadel-card" data-testid="genesis-command-card">
            <div className="card-top"><span>GENESIS COMMAND</span><b className="build-live"><span
                className="live-dot"/> BUILDING</b></div>
            <div className="citadel-stage" data-testid="citadel-build-stage">
                <img className="citadel-img" src={imageUrls.citadel} alt=""/>
                <div className="citadel-grid"/>
                <div className="citadel-scan"/>
                <div className="citadel-blocks" aria-hidden="true">{[0, 1, 2, 3, 4, 5].map((i) => <i key={i}
                                                                                                     style={{animationDelay: `${i * 0.4}s`}}/>)}</div>
                <div className="citadel-tag"><span className="live-dot"/> CONSTRUCTION LIVE</div>
            </div>
            <div className="countdown" data-testid="launch-countdown">
                <div className="countdown-head"><span className="live-dot"/> GENESIS LAUNCH — 01 NOV 2026</div>
                <div className="countdown-grid">
                    <div><b data-testid="countdown-days">{d}</b><small>DAYS</small></div>
                    <div><b data-testid="countdown-hours">{pad(h)}</b><small>HRS</small></div>
                    <div><b data-testid="countdown-mins">{pad(m)}</b><small>MIN</small></div>
                    <div><b data-testid="countdown-secs">{pad(s)}</b><small>SEC</small></div>
                </div>
            </div>
            <div className="build-progress" data-testid="citadel-build-progress">
                <div className="build-head">
                    <AnimatePresence mode="wait">
                        <motion.span key={label} initial={{opacity: 0, y: 8}} animate={{opacity: 1, y: 0}}
                                     exit={{opacity: 0, y: -8}} transition={{duration: .4}}>{label}</motion.span>
                    </AnimatePresence>
                    <b>{pct}%</b>
                </div>
                <div className="build-bar">
                    <motion.i animate={{width: `${pct}%`}} transition={{duration: 1.1, ease: "easeInOut"}}/>
                </div>
                <AnimatePresence mode="wait">
                    <motion.small key={sub} initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}
                                  transition={{duration: .4}}>{sub}</motion.small>
                </AnimatePresence>
            </div>
            <div className="command-data">
                <span>CORE <b>ONLINE</b></span><span>SECURITY <b>ACTIVE</b></span><span>ACCESS <b>SOON</b></span></div>
        </div>
    );
}

function SignupForm() {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        try {
            const res = await axios.post(`${API}/signup`, {email});
            setStatus({type: "ok", text: res.data.message});
            setEmail("");
        } catch (err) {
            setStatus({type: "err", text: err.response?.data?.detail || "Something went wrong. Please try again."});
        } finally {
            setLoading(false);
        }
    };
    return (
        <form className="signup-form" onSubmit={submit} noValidate data-testid="early-access-signup-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="Enter your email for Genesis access" data-testid="signup-email-input"/>
            <button type="submit" className="button-primary" disabled={loading}
                    data-testid="signup-submit-button">{loading ? "SECURING..." : "REQUEST ACCESS"} <ArrowUpRight
                size={16}/></button>
            {status && <div className={`signup-msg ${status.type}`} data-testid="signup-message">{status.text}</div>}
        </form>
    );
}

function Home() {
    const [openFaq, setOpenFaq] = useState(null);
    const [theme, setTheme] = useState(() => (typeof window !== "undefined" && localStorage.getItem("tv-theme")) || "dark");
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        localStorage.setItem("tv-theme", theme);
    }, [theme]);
    useEffect(() => {
        axios.get(`${API}/`).then((response) => console.log(response.data.message)).catch((error) => console.error(error, "errored out requesting / api"));
    }, []);
    return <main className="site-shell" data-testid="tradverse-coming-soon-page">
        <div className="genesis-bar" data-testid="genesis-status-bar"><span className="live-dot"/> <span>TRADVERSE GENESIS</span><span
            className="bar-sep">/</span><span>PRIVATE ACCESS IS OPENING SOON</span></div>
        <nav className="top-nav" data-testid="main-navigation"><a className="brand" href="#tv-genesis"
                                                                  data-testid="brand-home-link"><span
            className="brand-mark">TV</span><span>TRADVERSE<small>FINANCIAL CITADEL</small></span></a>
            <div className="nav-links"><a href="#tv-architecture" data-testid="nav-architecture-link">ARCHITECTURE</a><a
                href="#terminal" data-testid="nav-terminal-link">MODULES</a><a href="#faq-section"
                                                                               data-testid="nav-faq-link">FAQ</a></div>
            <div className="nav-actions">
                <button className="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        aria-label="Toggle theme" data-testid="theme-toggle-button">{theme === "dark" ?
                    <Sun size={16}/> : <Moon size={16}/>}</button>
                <a className="nav-cta" href="#tv-access" data-testid="nav-access-link">GENESIS ACCESS <ArrowUpRight
                    size={15}/></a></div>
        </nav>

        <section id="tv-genesis" className="hero-section" data-testid="hero-section">
            <div className="hero-photo" style={{backgroundImage: `url(${imageUrls.hero})`}}/>
            <div className="hero-grid"/>
            <div className="hero-inner"><Reveal>
                <div className="kicker">TRADVERSE / GENESIS ACCESS</div>
                <div className="hero-layout">
                    <div>
                        <div className="coming-label">COMING SOON</div>
                        <h1>THE CITADEL<br/><span>IS BEING BUILT.</span></h1><p className="hero-lead">Get early access
                        before the public launch.</p>
                        <div className="hero-actions"><a className="button-primary" href="#tv-access"
                                                         data-testid="join-early-access-button">JOIN EARLY
                            ACCESS <ArrowUpRight size={17}/></a><a className="button-ghost" href="#tv-architecture"
                                                                   data-testid="explore-architecture-button">EXPLORE THE
                            ARCHITECTURE <ArrowRight size={15}/></a></div>
                        <div className="trust-strip" data-testid="trust-strip"><span>SECURITY FIRST</span><i/><span>PRODUCT IN DEVELOPMENT</span><i/><span>EARLY ACCESS</span>
                        </div>
                    </div>
                    <CitadelBuild/></div>
            </Reveal></div>
        </section>

        <div className="ticker-wrapper" data-testid="market-ticker">
            <div
                className="ticker-track">BTC/USDT <b>$67,420.10</b><span>+2.84%</span> ETH/USDT <b>$3,841.22</b><span>+1.96%</span> SOL/USDT <b>$168.31</b><span>+4.12%</span> TRX/USDT <b>$0.12</b><span>+0.74%</span> SECURITY
                GATEWAY / BUILDING
            </div>
        </div>

        <section id="tv-architecture" className="section architecture-section" data-testid="architecture-section">
            <Reveal>
                <div className="section-head">
                    <div className="kicker">WHAT IS COMING</div>
                    <h2>ONE CITADEL. MULTIPLE LAYERS.</h2><p>A visual preview of the infrastructure being built behind
                    Tradverse.</p></div>
            </Reveal>
            <div
                className="arch-grid">{[["YIELD ENGINE", "Structured cycles, automated distributions and controlled positions.", CircleDollarSign], ["SOVEREIGN VAULT", "Capital routing designed around security and transparency.", Gem], ["EXECUTION TERMINAL", "Institutional-style execution and liquidity intelligence.", BarChart3], ["PASSKEY SECURITY", "Hardware-backed access and cryptographic protection.", Fingerprint], ["MULTI-CHAIN NETWORK", "A connected financial architecture across supported networks.", Network], ["DIGITAL LEDGER", "Structured records designed for clarity and accountability.", ScrollText]].map(([title, text, Icon], index) =>
                <Reveal key={title}>
                    <article className="arch-card" data-testid={`architecture-card-${index + 1}`}><FeatureArt
                        src={featureImages[title]} label={title}/>
                        <div className={"flex items-center gap-2"}>
                            <div className="arch-icon">
                                <Icon size={25}/>
                            </div>
                            <span>0{index + 1}</span>
                            <h3>{title}</h3>
                        </div>
                        <p>{text}</p></article>
                </Reveal>)}</div>
        </section>

        <section className="feature-section" data-testid="feature-showcase-section"><Reveal>
            <div className="feature-grid">
                <div>
                    <div className="kicker">MILESTONE GROWTH CYCLES</div>
                    <h2>Secure Fixed Daily Yields <br/>Across 20 to 100 Days</h2><p>Deploy capital between $10 to $100
                    USDT into structured financial cycles. Experience fully automated daily yield distributions straight
                    to your wallet with strict single-position risk controls.</p>
                    <div className="stat-row">
                        <div><small>DURATION POOLS</small><b>20 - 100 Days</b></div>
                        <div><small>DEPOSIT RANGE</small><b>$10 - $100</b></div>
                        <div><small>YIELD TYPE</small><b>Automated Daily</b></div>
                    </div>
                </div>
                <Visual type="chart"/></div>
        </Reveal><Reveal>
            <div className="feature-grid reverse">
                <div>
                    <div className="kicker">THE MOST SECURE & GENUINE FINANCIAL CITADEL IN HISTORY</div>
                    <h2>Earn Sovereign Yield on <br/>Invested Liquidity</h2><p>Initialize a sovereign Tradverse Treasury
                    Node and harvest continuous compounding yields with institutional transparency and absolute
                    security. Liquidate anytime.</p><a className="button-primary" href="#terminal"
                                                       data-testid="initialize-vault-button">INITIALIZE
                    VAULT <ArrowUpRight size={17}/></a>
                    <div className="fine-print">When investing, your capital is secured within verified cryptographic
                        bounds.<br/>Enabled liquidity pools route through institutional money market reserve funds.
                        Yield distribution applies strictly to verified citadel accounts. <u>Protocols apply</u>.
                    </div>
                </div>
                <Visual type="vault"/></div>
        </Reveal></section>

        <section className="feature-section investment-section" data-testid="investment-account-section"><Reveal>
            <div className="feature-grid reverse">
                <div><h2>Start investing with <br/>Zero Commissions</h2><p>Experience a superior start with our <b>$50
                    Welcome Offer</b> and a <b>Referral Offer</b> of up to <b>$5,000 worth of referral points</b> on
                    becoming a <b>Tradverse affiliate partner.</b></p>
                    <div className="hero-actions"><a className="button-primary" href="#terminal"
                                                     data-testid="start-investing-button">START INVESTING</a><a
                        className="button-ghost" href="#terminal" data-testid="learn-more-button">Learn More <ArrowRight
                        size={15}/></a></div>
                </div>
                <Visual type="terminal"/></div>
        </Reveal><Reveal>
            <div className="account-offers"><h2>Unlock more with your Investment Account</h2>
                <div className="offer-grid">
                    <article><h3>Deposit Offer</h3><a href="#terminal" data-testid="deposit-offer-link">Get
                        Offer <ArrowRight size={14}/></a></article>
                    <article><h3>Refer a friend and earn</h3><a href="#terminal" data-testid="invite-friends-link">Invite
                        Friends <ArrowRight size={14}/></a></article>
                </div>
            </div>
        </Reveal></section>

        <section className="execution-section" data-testid="execution-vector-section"><Reveal>
            <div className="execution-grid">
                <div><h2>Optimize Every <br/>Execution Vector</h2><p>Direct interface access across 1400+ institutional
                    corridors with compressed spreads and absolute fee elimination. Strive for optimal price discovery
                    on every block trade.</p><a className="button-primary" href="#terminal"
                                                data-testid="execution-node-button">OPEN CITADEL NODE <ArrowUpRight
                    size={17}/></a>
                    <div className="fine-print">Derivative executions carry advanced systemic parameters. <u>*Protocol
                        Parameters Apply</u>.
                    </div>
                </div>
                <Visual type="terminal"/></div>
        </Reveal>
            <div className="asset-pills"
                 data-testid="execution-asset-pills">{["Equities VAULT", "Commodities POOL", "Forex CORRIDOR", "ETFs VAULT", "Indices POOL", "Thematics POOL", "Digital Assets POOL"].map((x, i) =>
                <span key={x} className={i === 0 ? "active" : ""}>{x}</span>)}</div>
        </section>

        <section className="why-section" data-testid="why-choose-section"><Reveal>
            <div className="section-head"><h2>Why Choose Tradverse</h2><p>Elevate your wealth preservation with
                decentralized institutional infrastructure and absolute security.</p></div>
        </Reveal>
            <div className="why-grid"><Reveal>
                <article className="why-feature">
                    <div>
                        <div className="kicker">UNIFIED SOVEREIGN INFRASTRUCTURE</div>
                        <h3>Trade. Invest. Unified Citadel Core</h3><p>Allocate capital across global asset classes with
                        institutional backing and zero structural fees. Execute high-leverage block positioning across
                        1,400+ secured liquidity channels through a completely verified cryptographic ledger.</p></div>
                    <FeatureArt src={imageUrls.analytics} label="VERIFIED LEDGER"/></article>
            </Reveal><Reveal>
                <article className="why-card"><FeatureArt src={imageUrls.military} label="MILITARY DEFENSE"/>
                    <div className="why-icon"><ShieldCheck size={25}/></div>
                    <div className="kicker">MILITARY DEFENSE</div>
                    <h3>Absolute Sovereign Security</h3><p>Rest assured, your liquidity remains fully safeguarded inside
                        Tier-1 sovereign vaults and isolated multi-sig escrow accounts completely insulated from
                        operational corporate reserves.</p></article>
            </Reveal><Reveal>
                <article className="why-card"><FeatureArt src={imageUrls.compliance} label="SOVEREIGN COMPLIANCE"/>
                    <div className="why-icon"><Network size={25}/></div>
                    <div className="kicker">SOVEREIGN COMPLIANCE</div>
                    <h3>Decentralized Trust &amp; Regulation</h3><p>Tradverse architectures operate under cryptographic
                        compliance frameworks, intersecting traditional regulatory standards with decentralized
                        multi-sig verification protocols.</p></article>
            </Reveal></div>
        </section>

        <section className="dark-terminal" id="terminal" data-testid="terminal-section"><Reveal>
            <div className="terminal-copy">
                <div className="kicker">SOVEREIGN ARCHITECTURE SUITE</div>
                <h2>Pillars of Uncompromised Wealth</h2><p>Explore the engineering breakthroughs powering the Tradverse
                ecosystem with immersive transitions.</p><a className="button-primary" href="#tv-access"
                                                            data-testid="open-citadel-node-button">OPEN CITADEL
                NODE <ArrowUpRight size={17}/></a></div>
            <Visual type="terminal"/></Reveal>
            <div className="asset-pills"
                 data-testid="asset-pills">{["Equities VAULT", "Commodities POOL", "Forex CORRIDOR", "ETFs VAULT", "Indices POOL", "Thematics POOL", "Digital Assets POOL"].map((x, i) =>
                <span key={x} className={i === 0 ? "active" : ""}>{x}</span>)}</div>
        </section>

        <section className="modules-section" data-testid="modules-section"><Reveal>
            <div className="section-head left">
                <div className="kicker">WHAT IS COMING</div>
                <h2>THE BUILD IS ALREADY UNDERWAY.</h2><p>Follow the infrastructure as Tradverse moves toward public
                launch.</p></div>
        </Reveal>
            <div className="module-list">{modules.map(([tag, title, bold, text, bullets, Icon], i) => <Reveal
                key={title}>
                <article className="module-card" data-testid={`module-card-${i + 1}`}><FeatureArt
                    src={featureImages[tag]} label={tag}/>
                    <div className="module-body">
                        <div className="module-icon"><Icon size={25}/></div>
                        <div className="kicker">{tag}</div>
                        <h3>{title}</h3><p><strong>{bold}</strong> {text}</p>{bullets.length > 0 &&
                        <div className="module-bullets">{bullets.map((b) => <span key={b}><Check
                            size={13}/> {b}</span>)}</div>}</div>
                </article>
            </Reveal>)}</div>
        </section>

        <section className="inheritance-section" id="inheritance" data-testid="inheritance-section"><Reveal>
            <div>
                <div className="kicker">INDUSTRY-FIRST BREAKTHROUGH</div>
                <h2>Sovereign Dead-Man's Switch &amp; Nominee Inheritance Protocol</h2><p><strong>The Ultimate Crypto
                Inheritance Breakthrough:</strong> Historically, if a crypto holder faces unfortunate inactivity or
                long-term offline status, their digital wealth and investment vaults remain trapped forever. Tradverse
                shatters this industry limitation by launching the world's first autonomous, zero-trust Dead-Man's
                Switch protocol—allowing secure, login-free asset devolution to your pre-registered nominee.</p>
                <FeatureArt src={imageUrls.inheritance} label="NOMINEE DEVOLUTION"/></div>
            <div className="inheritance-cards">
                <article><FeatureArt src={imageUrls.zkp} label="ZKP CLAIMING"/><LockKeyhole size={20}/>
                    <h4>Zero-Knowledge Proof (ZKP) Claiming</h4><p>Nominees can claim locked vaults securely on our
                        standalone public portal (`claim.html`) using their registered email and a secret cryptographic
                        passkey without ever needing account login credentials.</p></article>
                <article><FeatureArt src={imageUrls.routing} label="MULTI-CHAIN ROUTING"/><GitBranch size={20}/>
                    <h4>Multi-Chain Autonomous Routing</h4><p>Real-time validation maps funds directly to secure TRC-20
                        or BEP-20 destination addresses backed by master-password re-authentication security locks.</p>
                </article>
            </div>
        </Reveal></section>

        <section className="faq-section" id="faq-section" data-testid="faq-section"><Reveal>
            <div className="section-head">
                <div className="kicker">HELP &amp; TELEMETRY SUPPORT</div>
                <h2>Recently Asked Questions</h2><p>Instant answers regarding our 8-hour core liquidity pools, security
                protocols, and sovereign asset management.</p></div>
        </Reveal>
            <div className="faq-grid">{faqs.map(([q, a], i) => <Reveal key={q}>
                <button className={`faq-card ${openFaq === i ? "open" : ""}`}
                        onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`faq-question-${i + 1}`}>
                    <span><strong>{q}</strong>{openFaq === i && <small>{a}</small>}</span><ChevronDown size={19}/>
                </button>
            </Reveal>)}</div>
        </section>

        <section className="conclusion-cta" id="citadel-launch" data-testid="citadel-launch-section"><Reveal>
            <div className="kicker">ENTER THE BASTION</div>
            <h2>The Most Secure &amp; Genuine <br/><span>Financial Citadel in History</span></h2><p>Connect your secure
            hardware passkey, configure your sovereign inheritance nominee across all independent architectural modules,
            and experience uncompromised wealth security.</p>
            <div className="footer-note">Tradverse Financial Citadel • 100% Verified &amp; Secure • Zero Counterparty Risk</div>
        </Reveal></section>
        <section className="final-cta" id="tv-access" data-testid="access-section"><Reveal>
            <div className="kicker">GENESIS ACCESS</div>
            <h2>BE FIRST THROUGH THE GATES.</h2><p>By joining, you are requesting launch information and early-access
            updates.</p><SignupForm/><a className="button-primary" href="#tv-genesis" data-testid="final-access-button">INITIALIZE
            SECURE WEB3 CONNECTION <ArrowUpRight size={17}/></a>
            <div className="footer-note">Tradverse Financial Citadel • 100% Verified &amp; Secure • Zero Counterparty
                Risk
            </div>
        </Reveal></section>
        <footer data-testid="page-footer"><span>TRADVERSE</span><span>THE MOST SECURE &amp; GENUINE FINANCIAL CITADEL IN HISTORY</span>
        </footer>
    </main>;
}

export default function App() {
    return <BrowserRouter><Routes><Route path="*" element={<Home/>}/></Routes></BrowserRouter>;
}